/**
 * "You have an unsaved voucher" -- asked before leaving it.
 *
 * With a sidebar and Ctrl+K always one click away, it became easy to walk
 * off a half-entered voucher and lose it. A screen with unsaved work calls
 * useUnsavedChanges(dirty); everything in the shell that navigates
 * (sidebar, search, Back/Home, bottom tabs) goes through `guard`, which
 * asks first. Closing or reloading the tab is covered by the browser's
 * own prompt.
 *
 * The browser's Back button is not covered: that needs React Router's
 * data router, which this app does not use yet.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react'
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useHotkeys } from '../hotkeys/HotkeyProvider'

interface UnsavedValue {
  /** Run `go` now, or after the user agrees to discard unsaved work. */
  guard: (go: () => void) => void
  setDirty: (dirty: boolean) => void
}

const Ctx = createContext<UnsavedValue>({
  guard: (go) => go(),
  setDirty: () => {},
})

function ConfirmScope({ onKeep }: { onKeep: () => void }) {
  // Suspends the screen's shortcuts while the question is open.
  useHotkeys('unsaved-confirm', [{ combo: 'escape', handler: onKeep, allowInInput: true }])
  return null
}

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const dirty = useRef(false)
  const [pending, setPending] = useState<null | (() => void)>(null)

  const setDirty = useCallback((d: boolean) => { dirty.current = d }, [])

  const guard = useCallback((go: () => void) => {
    if (!dirty.current) { go(); return }
    setPending(() => go)
  }, [])

  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if (!dirty.current) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onUnload)
    return () => window.removeEventListener('beforeunload', onUnload)
  }, [])

  const keep = useCallback(() => setPending(null), [])
  const discard = useCallback(() => {
    const go = pending
    dirty.current = false
    setPending(null)
    go?.()
  }, [pending])

  const value = useMemo(() => ({ guard, setDirty }), [guard, setDirty])

  return (
    <Ctx.Provider value={value}>
      {children}
      <Dialog open={!!pending} onClose={keep} aria-labelledby="unsaved-title">
        {pending && <ConfirmScope onKeep={keep} />}
        <DialogTitle id="unsaved-title">{t('unsaved.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('unsaved.body')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={discard} color="error">{t('unsaved.discard')}</Button>
          <Button onClick={keep} variant="contained" autoFocus>{t('unsaved.keep')}</Button>
        </DialogActions>
      </Dialog>
    </Ctx.Provider>
  )
}

/** Declare whether this screen holds unsaved work. Cleared when it unmounts. */
export function useUnsavedChanges(dirty: boolean) {
  const { setDirty } = useContext(Ctx)
  useEffect(() => { setDirty(dirty) }, [dirty, setDirty])
  useEffect(() => () => setDirty(false), [setDirty])
}

export function useGuard() {
  return useContext(Ctx).guard
}

/** navigate(), but asking first when the current screen has unsaved work. */
export function useGuardedNavigate() {
  const nav = useNavigate()
  const guard = useGuard()
  return useCallback((to: string | number) => {
    guard(() => (typeof to === 'number' ? nav(to) : nav(to)))
  }, [guard, nav])
}
